<template>
  <v-dialog
    :value="open"
    :fullscreen="supportFullscreen"
    max-width="470"
    content-class="support-dialog"
    scrollable
    @input="close"
  >
    <v-card
      class="support-chat"
      :class="{ 'support-chat-fullscreen': supportFullscreen }"
      role="region"
      aria-label="Suporte Vet Alfa"
    >
      <header class="support-header">
        <v-btn
          v-if="conversation || creating"
          icon
          class="support-header-action"
          aria-label="Voltar para conversas"
          @click="back"
          ><v-icon>{{ icons.back }}</v-icon></v-btn
        >
        <div v-else class="support-brand" aria-hidden="true">
          <v-icon color="white">{{ icons.chat }}</v-icon>
        </div>
        <div class="support-header-copy">
          <h2>{{ conversation ? "Atendimento" : "Suporte Vet Alfa" }}</h2>
          <p>
            {{
              adminViewer
                ? "Conversas que precisam de você"
                : "Estamos aqui para ajudar"
            }}
          </p>
        </div>
        <v-btn
          icon
          class="support-header-action"
          aria-label="Fechar suporte"
          @click="close"
          ><v-icon>{{ icons.close }}</v-icon></v-btn
        >
      </header>
      <v-progress-linear
        v-if="busy"
        indeterminate
        height="2"
        aria-label="Carregando"
      />
      <v-alert
        v-if="error"
        type="error"
        dense
        class="support-error"
        role="alert"
        >{{ error
        }}<v-btn
          v-if="activeError.method"
          small
          text
          rounded
          :disabled="busy || initializing"
          @click="retry"
          >Tentar novamente</v-btn
        ></v-alert
      >

      <v-card-text
        v-if="!conversation && !creating"
        class="support-body support-inbox"
      >
        <div class="support-welcome">
          <span class="support-eyebrow">{{
            admin ? "SUA CAIXA DE ENTRADA" : "VAMOS CONVERSAR"
          }}</span>
          <h3>
            {{ admin ? "Tudo em um só lugar." : "Olá! Como podemos ajudar?" }}
          </h3>
          <p>
            {{
              admin
                ? "Acompanhe e responda aos atendimentos."
                : "Converse com a nossa equipe, do seu jeito."
            }}
          </p>
        </div>
        <v-btn
          v-if="!admin"
          color="primary"
          block
          rounded
          depressed
          large
          class="support-new"
          :disabled="initializing"
          @click="newConversation"
          >Nova conversa<v-icon right size="19">{{
            icons.arrow
          }}</v-icon></v-btn
        >
        <div v-if="admin" class="support-inbox-tools">
          <label class="support-filter"
            ><span>Mostrar conversas</span
            ><select v-model="filter" @change="changeList"
              ><option
                v-for="item in filters"
                :key="item.value"
                :value="item.value"
                >{{ item.text }}</option
              ></select
            ></label
          >
        </div>
        <div v-if="conversations.length" class="support-section-label">
          {{ management ? "Atendimentos" : "Suas conversas"
          }}<span>{{ conversations.length }}</span>
        </div>
        <v-list class="support-conversation-list" aria-label="Conversas">
          <v-list-item
            v-for="item in conversations"
            :key="item.id"
            class="support-conversation-row"
            @click="select(item.id)"
          >
            <div class="support-avatar" aria-hidden="true">
              {{ management ? initials(item.name) : "VA" }}
            </div>
            <v-list-item-content>
              <v-list-item-title
                ><span>{{ management ? item.name : "Equipe Vet Alfa" }}</span
                ><span
                  v-if="unread(item)"
                  class="support-unread"
                  aria-label="Mensagem não lida"
              /></v-list-item-title>
              <v-list-item-subtitle class="support-preview">{{
                preview(item)
              }}</v-list-item-subtitle>
              <div class="support-row-meta">
                <span>{{
                  item.status === "closed" ? "Encerrada" : "Aberta"
                }}</span
                ><span v-if="management">{{ identityLabel(item) }}</span>
              </div>
            </v-list-item-content>
            <v-icon size="18" color="#8a96a3" aria-hidden="true">{{
              icons.arrow
            }}</v-icon>
          </v-list-item>
        </v-list>
        <div v-if="!busy && !conversations.length" class="support-empty">
          <span class="support-empty-icon"
            ><v-icon size="28">{{ icons.chat }}</v-icon></span
          ><strong>{{
            admin
              ? "Nenhum atendimento neste filtro"
              : "Sua próxima conversa começa aqui"
          }}</strong>
          <p>
            {{
              admin
                ? "As conversas iniciadas pelos usuários aparecerão aqui para você responder."
                : "Quando você iniciar um atendimento, ele aparece neste espaço."
            }}
          </p>
        </div>
        <v-btn v-if="listCursor" text rounded block @click="loadList(true)"
          >Mais conversas</v-btn
        >
        <p v-if="!admin" class="support-footnote">
          Abra o chat para consultar as respostas.<br />Não enviamos respostas
          por e-mail.
        </p>
      </v-card-text>

      <v-card-text
        v-else-if="creating && !admin"
        class="support-body support-new-form"
      >
        <div class="support-welcome">
          <span class="support-eyebrow">NOVO ATENDIMENTO</span>
          <h3>Conte com a gente.</h3>
          <p>
            {{
              uid
                ? "Escreva sua dúvida para começar."
                : "Antes de começar, como podemos chamar você?"
            }}
          </p>
        </div>
        <template v-if="!uid">
          <v-text-field
            v-model="name"
            label="Seu nome"
            maxlength="100"
            autocomplete="name"
            outlined
            rounded
            dense
            class="support-form-field"
          />
          <v-text-field
            v-model="email"
            label="Seu e-mail"
            type="email"
            maxlength="254"
            autocomplete="email"
            outlined
            rounded
            dense
            class="support-form-field"
          />
          <p class="support-identity-note">
            Prefira o e-mail usado no Vet Alfa. Volte a este chat para ver a
            resposta: não enviaremos e-mail. Se limpar os dados ou trocar de
            dispositivo, poderá perder o acesso a esta conversa.
          </p>
        </template>
        <v-textarea
          v-model="text"
          label="Sua mensagem"
          placeholder="Como podemos ajudar você hoje?"
          maxlength="5000"
          rows="4"
          outlined
          rounded
          class="support-form-field"
        />
        <v-btn
          color="primary"
          rounded
          depressed
          block
          large
          :disabled="busy || !validNew"
          @click="create"
          >Iniciar conversa<v-icon right size="18">{{
            icons.send
          }}</v-icon></v-btn
        >
      </v-card-text>

      <template v-else>
        <div
          v-if="conversation.viewerRole === 'admin'"
          class="support-metadata"
        >
          <div class="support-contact">
            <div class="support-avatar" aria-hidden="true">
              {{ initials(conversation.name) }}
            </div>
            <div>
              <strong>{{ conversation.name }}</strong
              ><span>{{ conversation.email }} · {{ conversation.origin }}</span>
            </div>
          </div>
          <span
            class="support-identity-badge"
            :class="{
              'support-identity-visitor': conversation.visitor !== false
            }"
            >{{ identityLabel(conversation) }}</span
          >
          <p
            v-if="conversation.visitor !== false"
            class="support-identity-warning"
          >
            Nome e e-mail informados pelo visitante não comprovam sua
            identidade.
          </p>
          <div class="support-assignment">
            <span>{{
              conversation.assignedName
                ? "Responsável: " + conversation.assignedName
                : "Sem responsável"
            }}</span>
            <div>
              <v-btn
                small
                text
                rounded
                :disabled="busy"
                @click="updateState({ assignToMe: true })"
                >Assumir</v-btn
              ><v-btn
                small
                outlined
                rounded
                :disabled="busy"
                @click="
                  updateState({
                    status: conversation.status === 'closed' ? 'open' : 'closed'
                  })
                "
                >{{
                  conversation.status === "closed" ? "Reabrir" : "Encerrar"
                }}</v-btn
              >
            </div>
          </div>
        </div>
        <v-card-text
          ref="history"
          class="support-body support-history"
          aria-live="polite"
          aria-relevant="additions"
        >
          <v-btn
            v-if="nextBefore"
            small
            text
            rounded
            block
            :loading="!!pendingHistory"
            @click="loadMessages(true)"
            >Mensagens anteriores</v-btn
          >
          <div class="support-history-intro">
            <span>Equipe Vet Alfa</span>
            <p>Este é o seu espaço para conversar com a gente.</p>
          </div>
          <article
            v-for="message in messages"
            :key="message.id"
            class="support-message"
            :class="{ 'support-message-own': own(message) }"
          >
            <strong class="support-author"
              >{{ message.author.name
              }}{{
                message.author.role === "admin" ? " · Equipe Vet Alfa" : ""
              }}</strong
            >
            <p class="support-text">
              <template v-for="(part, index) in messageParts(message.text)"
                ><button
                  v-if="part.url"
                  :key="index"
                  type="button"
                  class="support-link"
                  @click="openLink(part.url)"
                >
                  {{ part.text }}</button
                ><span v-else :key="index">{{ part.text }}</span></template
              >
            </p>
            <template v-for="attachment in message.attachments"
              ><img
                v-if="images[attachment.id]"
                :key="attachment.id"
                :src="images[attachment.id]"
                :alt="attachment.name"
                class="support-image"
              /><v-btn
                v-else
                :key="attachment.id"
                small
                text
                rounded
                @click="loadImage(attachment)"
                >Ver imagem: {{ attachment.name }}</v-btn
              ></template
            >
            <time class="support-time">{{ date(message.createdAt) }}</time>
          </article>
        </v-card-text>
        <div class="support-composer">
          <p
            v-if="conversation.status === 'closed'"
            class="support-composer-note"
          >
            Atendimento encerrado. Uma nova mensagem retoma a conversa.
          </p>
          <div class="support-composer-box">
            <div v-if="file" class="support-attachment-preview">
              <img
                v-if="filePreview"
                :src="filePreview"
                alt="Prévia da imagem selecionada"
              />
              <div>
                <strong>{{ file.name }}</strong
                ><span>Pronta para enviar</span>
              </div>
              <v-btn
                icon
                small
                aria-label="Remover imagem"
                :disabled="busy || !!pendingMessage"
                @click="removeFile"
                ><v-icon size="18">{{ icons.close }}</v-icon></v-btn
              >
            </div>
            <label class="support-sr-only" for="support-message-input"
              >Mensagem</label
            >
            <textarea
              id="support-message-input"
              v-model="text"
              placeholder="Escreva sua mensagem…"
              rows="2"
              maxlength="5000"
              :disabled="busy || !!pendingMessage"
            />
            <div class="support-composer-actions">
              <input
                ref="attachmentInput"
                class="support-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                aria-label="Adicionar imagem (até 5 MB)"
                :disabled="busy || !!pendingMessage"
                @change="chooseFile"
              /><v-btn
                icon
                class="support-attach"
                aria-label="Adicionar imagem (até 5 MB)"
                title="Adicionar imagem (até 5 MB)"
                :disabled="busy || !!pendingMessage"
                @click="$refs.attachmentInput.click()"
                ><v-icon size="22">{{ icons.attach }}</v-icon></v-btn
              ><span class="support-composer-hint">Texto ou imagem</span
              ><v-btn
                icon
                color="primary"
                class="support-send"
                aria-label="Enviar"
                title="Enviar"
                :loading="busy"
                :disabled="(!text.trim() && !file) || !!pendingMessage"
                @click="send"
                ><v-icon size="21">{{ icons.send }}</v-icon></v-btn
              >
            </div>
          </div>
          <p class="support-footnote">Consulte as respostas aqui no chat.</p>
        </div>
      </template>
      <v-dialog
        :value="!!pendingLink"
        max-width="440"
        content-class="support-link-dialog"
        @input="cancelLink"
        ><v-card v-if="pendingLink" class="support-link-card"
          ><v-card-title>Abrir link externo?</v-card-title
          ><v-card-text
            ><p>Confira o destino real antes de continuar:</p>
            <strong class="support-destination">{{
              pendingLink.hostname
            }}</strong>
            <p class="support-destination">{{ pendingLink.href }}</p>
            <p>
              Links enviados no chat podem ser golpes. Não forneça senhas,
              códigos nem faça pagamentos sem confirmar por um canal oficial. O
              Vet Alfa não verificou a segurança deste destino.
            </p></v-card-text
          ><v-card-actions
            ><v-spacer /><v-btn text rounded @click="cancelLink">Cancelar</v-btn
            ><v-btn color="primary" rounded depressed @click="confirmLink"
              >Abrir destino</v-btn
            ></v-card-actions
          ></v-card
        ></v-dialog
      >
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from "vuex";
import {
  parseSupportUrl,
  supportMessageParts,
  openSupportUrl
} from "@/platform/support-links.js";
import {
  mdiArrowLeft,
  mdiClose,
  mdiPaperclip,
  mdiSend,
  mdiChatOutline,
  mdiChevronRight
} from "@mdi/js";
import api, {
  supportId,
  clearSupportSession
} from "@/services/support.services.js";
export default {
  name: "VetalfaSupportChat",
  data: () => ({
    icons: {
      back: mdiArrowLeft,
      close: mdiClose,
      attach: mdiPaperclip,
      send: mdiSend,
      chat: mdiChatOutline,
      arrow: mdiChevronRight
    },
    admin: false,
    initializing: false,
    management: false,
    conversations: [],
    conversation: null,
    messages: [],
    images: {},
    creating: false,
    name: "",
    email: "",
    text: "",
    file: null,
    filePreview: null,
    busy: false,
    errors: {},
    filter: "open",
    listCursor: null,
    listRequest: 0,
    imageRequests: {},
    nextBefore: null,
    generation: 0,
    listTimer: null,
    messageTimer: null,
    pendingLink: null,
    pendingMessage: null,
    pendingConversation: null,
    deepLink: null,
    fetching: false,
    pendingHistory: null,
    filters: [
      { text: "Abertas", value: "open" },
      { text: "Encerradas", value: "closed" },
      { text: "Todas", value: "" }
    ]
  }),
  computed: {
    ...mapGetters("support", ["open", "conversationId"]),
    ...mapGetters("general", ["user"]),
    activeError() {
      // Background polling must not hide a failed send or its retry action.
      const sources = [
        "session",
        "identity",
        "send",
        "create",
        "state",
        "file"
      ];
      const source =
        sources.find(key => this.errors[key]) || Object.keys(this.errors)[0];
      return this.errors[source] || null;
    },
    error() {
      return this.activeError ? this.activeError.message : "";
    },
    supportFullscreen() {
      const viewport = this.$vuetify.breakpoint;
      // iPhone landscape exceeds xsOnly. Geometry also handles small browser
      // windows consistently, without UA guessing or touching native status bars.
      return (
        viewport.xsOnly || (viewport.width <= 960 && viewport.height <= 500)
      );
    },
    adminViewer() {
      return (
        this.admin ||
        !!(this.conversation && this.conversation.viewerRole === "admin")
      );
    },
    uid() {
      return (this.user || {}).uid || "";
    },
    validNew() {
      return (
        this.text.trim() &&
        (this.uid ||
          (this.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)))
      );
    }
  },
  watch: {
    file(value) {
      this.refreshFilePreview(value);
    },
    open(value) {
      if (value) this.start();
      else this.stop();
    },
    uid(value, previous) {
      // A cold boot can restore the persisted user after mounted() started the
      // deep link. Keep that request until the server actually loads it, then
      // reopen after the close watcher has flushed. Account switches discard it.
      const pending = !previous ? this.deepLink : null;
      this.resetIdentity();
      this.deepLink = pending;
      this.$nextTick(() => this.resumeDeepLink());
    },
    conversationId(value) {
      // start() consumes the latest deep link after identity is established.
      // Selecting during initialization would invalidate its generation token.
      if (this.open && value && !this.initializing) this.select(value);
    }
  },
  mounted() {
    document.addEventListener("visibilitychange", this.visibility);
    document.addEventListener("pause", this.stopTimers);
    document.addEventListener("resume", this.visibility);
    window.addEventListener("vetalfa:logout", this.resetIdentity);
    window.addEventListener(
      "vetalfa:support-session-expired",
      this.sessionExpired
    );
    const query = new URLSearchParams(window.location.search);
    const hashQuery = new URLSearchParams(
      window.location.hash.split("?")[1] || ""
    );
    this.deepLink =
      query.get("supportConversation") || hashQuery.get("supportConversation");
    this.resumeDeepLink();
    if (this.open) this.start();
  },
  beforeDestroy() {
    this.releaseFilePreview();
    this.stop();
    document.removeEventListener("visibilitychange", this.visibility);
    document.removeEventListener("pause", this.stopTimers);
    document.removeEventListener("resume", this.visibility);
    window.removeEventListener("vetalfa:logout", this.resetIdentity);
    window.removeEventListener(
      "vetalfa:support-session-expired",
      this.sessionExpired
    );
  },
  methods: {
    setError(source, message, method = null, args = []) {
      this.$set(this.errors, source, { message, method, args });
    },
    clearError(source) {
      this.$delete(this.errors, source);
    },
    initials(name) {
      return (
        (typeof name === "string" ? name : "?")
          .trim()
          .split(/\s+/)
          .slice(0, 2)
          .map(part => part.charAt(0))
          .join("")
          .toUpperCase() || "?"
      );
    },
    releaseFilePreview() {
      if (this.filePreview) URL.revokeObjectURL(this.filePreview);
      this.filePreview = null;
    },
    refreshFilePreview(file) {
      this.releaseFilePreview();
      if (!file) {
        if (this.$refs.attachmentInput) this.$refs.attachmentInput.value = "";
        return;
      }
      if (
        file.size > 5 * 1024 * 1024 ||
        !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(
          file.type
        )
      ) {
        this.file = null;
        this.setError(
          "file",
          "Escolha uma imagem JPG, PNG, WEBP ou GIF de até 5 MB."
        );
        return;
      }
      this.clearError("file");
      this.filePreview = URL.createObjectURL(file);
    },
    chooseFile(event) {
      this.file = (event.target.files && event.target.files[0]) || null;
    },
    removeFile() {
      this.file = null;
      this.releaseFilePreview();
      if (this.$refs.attachmentInput) this.$refs.attachmentInput.value = "";
    },
    identityLabel(conversation) {
      return conversation && conversation.visitor === false
        ? "Usuário autenticado"
        : "Visitante · e-mail não verificado";
    },
    messageParts: supportMessageParts,
    openLink(url) {
      this.pendingLink = parseSupportUrl(url);
    },
    cancelLink() {
      this.pendingLink = null;
    },
    confirmLink() {
      const pending = this.pendingLink;
      this.pendingLink = null;
      if (pending) openSupportUrl(pending.href);
    },
    sessionExpired() {
      this.stop();
      this.setError(
        "session",
        "Sua sessão de visitante expirou. Inicie uma nova conversa para voltar a falar com a equipe.",
        "start"
      );
    },
    resumeDeepLink() {
      if (this.deepLink && this.uid) {
        this.$store.dispatch("support/open", this.deepLink);
      }
    },
    resetIdentity() {
      this.deepLink = null;
      this.stop();
      clearSupportSession();
      this.admin = false;
      this.management = false;
      this.name = "";
      this.email = "";
      this.$store.dispatch("support/close");
    },
    close() {
      this.deepLink = null;
      this.pendingLink = null;
      this.$store.dispatch("support/close");
    },
    stopTimers() {
      clearInterval(this.listTimer);
      clearInterval(this.messageTimer);
      this.listTimer = null;
      this.messageTimer = null;
    },
    stop() {
      this.initializing = false;
      this.pendingLink = null;
      this.stopTimers();
      this.generation++;
      this.conversation = null;
      this.conversations = [];
      this.messages = [];
      this.images = {};
      this.imageRequests = {};
      this.errors = {};
      this.text = "";
      this.file = null;
      this.creating = false;
      this.busy = false;
      this.fetching = false;
      this.pendingHistory = null;
      this.pendingMessage = null;
      this.pendingConversation = null;
    },
    async start() {
      // Both mounted() and the open watcher can run in the same tick.
      if (this.initializing) return;
      this.initializing = true;
      this.stopTimers();
      const epoch = ++this.generation;
      this.errors = {};
      this.busy = true;
      this.admin = false;
      this.management = false;
      try {
        const identity = await api.get("/identity");
        if (epoch !== this.generation) return;
        this.admin = !!identity.supportAdmin;
        this.management = this.admin;
        await this.loadList();
        if (epoch !== this.generation) return;
        this.initializing = false;
        const requested = this.conversationId || (this.uid && this.deepLink);
        if (requested) await this.select(requested);
        this.visibility(false);
      } catch (_) {
        if (epoch === this.generation)
          this.setError(
            "identity",
            "Não foi possível abrir o suporte.",
            "start"
          );
      } finally {
        if (epoch === this.generation) {
          this.initializing = false;
          this.busy = false;
        }
      }
    },
    visibility(refresh = true) {
      this.stopTimers();
      if (!this.open || this.initializing || document.hidden) return;
      if (refresh) {
        this.loadList();
        if (this.conversation) this.loadMessages();
      }
      this.listTimer = setInterval(() => this.loadList(), 30000);
      this.messageTimer = setInterval(() => {
        if (this.conversation && !this.busy) this.loadMessages();
      }, 3000);
    },
    async loadList(more = false) {
      const request = ++this.listRequest;
      const epoch = this.generation;
      const management = this.management;
      const filter = this.filter;
      try {
        const params = new URLSearchParams();
        if (more && this.listCursor) params.set("cursor", this.listCursor);
        if (management && filter) params.set("status", filter);
        const result = await api.get(
          (management ? "/management/conversations" : "/conversations") +
            "?" +
            params
        );
        if (
          request !== this.listRequest ||
          epoch !== this.generation ||
          management !== this.management ||
          filter !== this.filter
        )
          return;
        const conversations = more
          ? this.conversations.concat(result.conversations)
          : result.conversations;
        this.conversations = Array.from(
          new Map(conversations.map(item => [item.id, item])).values()
        );
        this.listCursor = result.nextCursor;
        this.clearError("list");
      } catch (_) {
        if (
          request === this.listRequest &&
          epoch === this.generation &&
          management === this.management &&
          filter === this.filter
        )
          this.setError(
            "list",
            "Não foi possível atualizar as conversas.",
            "loadList",
            [more]
          );
      }
    },
    changeList() {
      this.generation++;
      this.clearError("list");
      this.conversations = [];
      this.listCursor = null;
      this.loadList();
    },
    unread(item) {
      return (
        item.lastSequence >
        (item.viewerRole === "admin"
          ? item.adminReadSequence || 0
          : item.participantReadSequence || 0)
      );
    },
    preview(item) {
      return typeof item.lastMessage === "string"
        ? item.lastMessage
        : (item.lastMessage || {}).text || "Imagem ou nova conversa";
    },
    own(message) {
      return (
        message.author.role ===
        (this.conversation && this.conversation.viewerRole === "admin"
          ? "admin"
          : "participant")
      );
    },
    date(value) {
      const d = new Date(
        value && value._seconds ? value._seconds * 1000 : value
      );
      return isNaN(d.getTime()) ? "" : d.toLocaleString("pt-BR");
    },
    back() {
      this.deepLink = null;
      this.pendingLink = null;
      this.generation++;
      this.conversation = null;
      this.messages = [];
      this.images = {};
      this.imageRequests = {};
      this.errors = {};
      this.creating = false;
      this.text = "";
      this.file = null;
      this.busy = false;
      this.fetching = false;
      this.pendingHistory = null;
      this.pendingMessage = null;
      this.pendingConversation = null;
      this.loadList();
    },
    newConversation() {
      if (this.admin || this.initializing) return;
      this.back();
      this.creating = true;
      this.pendingConversation = supportId();
    },
    async create() {
      if (this.admin || this.initializing || !this.validNew || this.busy)
        return;
      this.busy = true;
      this.clearError("create");
      const epoch = this.generation;
      try {
        const result = await api.post("/conversations", {
          clientConversationId: this.pendingConversation,
          name: this.name.trim(),
          email: this.email.trim(),
          origin: this._getPlatform()
        });
        if (epoch !== this.generation) return;
        this.conversation = result.conversation;
        this.messages = [];
        this.creating = false;
        this.nextBefore = null;
        this.busy = false;
        await this.send();
        this.loadList();
      } catch (_) {
        if (epoch === this.generation)
          this.setError(
            "create",
            "Não foi possível iniciar a conversa. Tente novamente.",
            "create"
          );
      } finally {
        if (epoch === this.generation) this.busy = false;
      }
    },
    async select(id) {
      this.generation++;
      this.busy = false;
      this.conversation = { id };
      this.messages = [];
      this.images = {};
      this.imageRequests = {};
      this.nextBefore = null;
      this.text = "";
      this.file = null;
      this.pendingMessage = null;
      this.fetching = false;
      this.pendingHistory = null;
      this.creating = false;
      this.errors = {};
      await this.loadMessages();
    },
    async loadMessages(older = false) {
      if (!this.conversation || (older && !this.nextBefore)) return;
      if (this.fetching) {
        // Preserve an explicit history click while polling or its read receipt
        // is pending. Keep it scoped to this conversation and session generation.
        if (older)
          this.pendingHistory = {
            generation: this.generation,
            id: this.conversation.id
          };
        return;
      }
      this.fetching = true;
      const epoch = this.generation;
      const id = this.conversation.id;
      try {
        const query = older
          ? "?before=" + this.nextBefore
          : this.messages.length
          ? "?after=" + this.messages[this.messages.length - 1].sequence
          : "";
        const result = await api.get(
          "/conversations/" + encodeURIComponent(id) + query
        );
        if (
          epoch !== this.generation ||
          !this.conversation ||
          id !== this.conversation.id
        )
          return;
        this.conversation = result.conversation;
        this.clearError(older ? "history" : "messages");
        if (this.deepLink === id) this.deepLink = null;
        const byId = new Map(
          this.messages.concat(result.messages).map(m => [m.id, m])
        );
        this.messages = Array.from(byId.values()).sort(
          (a, b) => a.sequence - b.sequence
        );
        if (older || !query) this.nextBefore = result.nextBefore;
        const readField =
          this.conversation.viewerRole === "admin"
            ? "adminReadSequence"
            : "participantReadSequence";
        const newest = this.messages.length
          ? this.messages[this.messages.length - 1].sequence
          : 0;
        if (newest > (this.conversation[readField] || 0)) {
          try {
            await api.post(
              "/conversations/" + encodeURIComponent(id) + "/read",
              {
                sequence: newest
              }
            );
            if (epoch !== this.generation) return;
            this.$set(this.conversation, readField, newest);
            this.clearError("read");
          } catch (_) {
            if (epoch !== this.generation) return;
            this.setError(
              "read",
              "Mensagens carregadas, mas não foi possível marcar a leitura.",
              "loadMessages"
            );
          }
        } else {
          this.clearError("read");
        }
        // Retry missing images even when the incremental response has no new messages.
        this.messages.forEach(message =>
          (message.attachments || []).forEach(attachment => {
            if (!this.images[attachment.id]) this.loadImage(attachment);
          })
        );
        if (!older && result.messages.length && epoch === this.generation)
          this.$nextTick(() => {
            if (this.$refs.history)
              this.$refs.history.scrollTop = this.$refs.history.scrollHeight;
          });
      } catch (_) {
        if (epoch === this.generation)
          this.setError(
            older ? "history" : "messages",
            "Não foi possível atualizar as mensagens.",
            "loadMessages",
            [older]
          );
      } finally {
        if (epoch === this.generation) {
          this.fetching = false;
          const pending = this.pendingHistory;
          this.pendingHistory = null;
          if (
            pending &&
            pending.generation === epoch &&
            pending.id === id &&
            this.conversation &&
            this.conversation.id === id &&
            this.nextBefore
          )
            await this.loadMessages(true);
        }
      }
    },
    async send() {
      if (this.busy || (!this.text.trim() && !this.file)) return;
      this.busy = true;
      this.clearError("send");
      this.clearError("file");
      const epoch = this.generation;
      const id = this.conversation.id;
      let uploading = false;
      try {
        if (!this.pendingMessage)
          this.pendingMessage = {
            clientMessageId: supportId(),
            text: this.text.trim(),
            attachments: []
          };
        if (this.file && !this.pendingMessage.attachments.length) {
          if (
            this.file.size > 5 * 1024 * 1024 ||
            !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(
              this.file.type
            )
          )
            throw new Error("image");
          const file = this.file;
          const data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = reject;
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.readAsDataURL(file);
          });
          if (epoch !== this.generation) return;
          uploading = true;
          const uploaded = await api.post(
            "/conversations/" + encodeURIComponent(id) + "/attachments",
            { data, mimeType: file.type, name: file.name }
          );
          if (epoch !== this.generation) return;
          uploading = false;
          this.pendingMessage.attachments = [uploaded.attachment.id];
        }
        await api.post(
          "/conversations/" + encodeURIComponent(id) + "/messages",
          this.pendingMessage
        );
        if (epoch !== this.generation) return;
        this.pendingMessage = null;
        this.text = "";
        this.file = null;
        await this.loadMessages();
        this.loadList();
      } catch (e) {
        // A definitive upload validation failure happened before any message
        // write. Allow replacing the file; uncertain network outcomes keep the
        // immutable pending message so retries cannot duplicate delivery.
        if (
          uploading &&
          e.response &&
          [400, 413].includes(e.response.status) &&
          epoch === this.generation
        ) {
          this.pendingMessage = null;
          this.setError(
            "file",
            "Imagem recusada pelo suporte. Remova o arquivo e escolha uma imagem JPG, PNG, WEBP ou GIF de até 5 MB."
          );
          return;
        }
        if (e.message === "image" && epoch === this.generation)
          this.pendingMessage = null;
        if (epoch === this.generation)
          this.setError(
            e.message === "image" ? "file" : "send",
            e.message === "image"
              ? "Escolha uma imagem JPG, PNG, WEBP ou GIF de até 5 MB."
              : "Mensagem não enviada. Tente novamente para reenviá-la sem duplicar.",
            e.message === "image" ? null : "send"
          );
      } finally {
        if (epoch === this.generation) this.busy = false;
      }
    },
    async loadImage(attachment) {
      if (!this.conversation || this.imageRequests[attachment.id]) return;
      const epoch = this.generation;
      const source = "image:" + attachment.id;
      this.$set(this.imageRequests, attachment.id, true);
      try {
        const result = await api.get(
          "/conversations/" +
            encodeURIComponent(this.conversation.id) +
            "/attachments/" +
            encodeURIComponent(attachment.id)
        );
        if (epoch !== this.generation) return;
        if (
          !["image/jpeg", "image/png", "image/webp", "image/gif"].includes(
            result.mimeType
          )
        )
          throw new Error("Invalid image type");
        this.$set(
          this.images,
          attachment.id,
          "data:" + result.mimeType + ";base64," + result.data
        );
        this.clearError(source);
      } catch (_) {
        if (epoch === this.generation)
          this.setError(
            source,
            "Não foi possível carregar a imagem.",
            "loadImage",
            [attachment]
          );
      } finally {
        if (epoch === this.generation)
          this.$delete(this.imageRequests, attachment.id);
      }
    },
    async updateState(body) {
      if (this.busy) return;
      const epoch = this.generation;
      this.busy = true;
      try {
        await api.post(
          "/conversations/" +
            encodeURIComponent(this.conversation.id) +
            "/state",
          body
        );
        if (epoch === this.generation) {
          this.clearError("state");
          await this.loadMessages();
          this.loadList();
        }
      } catch (_) {
        if (epoch === this.generation)
          this.setError(
            "state",
            "Não foi possível atualizar o atendimento.",
            "updateState",
            [body]
          );
      } finally {
        if (epoch === this.generation) this.busy = false;
      }
    },
    retry() {
      const failure = this.activeError;
      if (this.busy || this.initializing || !failure || !failure.method) return;
      return this[failure.method](...failure.args);
    }
  }
};
</script>
<style scoped>
.support-chat {
  --support-safe-top: 0px;
  --support-safe-right: 0px;
  --support-safe-bottom: 0px;
  --support-safe-left: 0px;
  --support-safe-top: constant(safe-area-inset-top);
  --support-safe-right: constant(safe-area-inset-right);
  --support-safe-bottom: constant(safe-area-inset-bottom);
  --support-safe-left: constant(safe-area-inset-left);
  --support-safe-top: env(safe-area-inset-top, 0px);
  --support-safe-right: env(safe-area-inset-right, 0px);
  --support-safe-bottom: env(safe-area-inset-bottom, 0px);
  --support-safe-left: env(safe-area-inset-left, 0px);
  box-sizing: border-box;
  height: min(780px, 92vh);
  display: flex;
  flex-direction: column;
  border-radius: 24px !important;
  overflow: hidden;
  color: #24364b;
  background: #fff;
}
.support-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 22px 20px;
  flex: 0 0 auto;
  color: #fff;
  background: linear-gradient(120deg, #153a52, #126c78);
}
.support-header-copy {
  flex: 1;
  min-width: 0;
}
.support-header h2 {
  margin: 0;
  color: #fff;
  font-size: 18px;
  line-height: 1.4;
  font-weight: 650;
  letter-spacing: -0.25px;
}
.support-header p {
  margin: 3px 0 0;
  color: #d7e9ed;
  font-size: 12px;
  line-height: 1.4;
}
.support-brand {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: #ffffff20;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
}
.support-header .support-header-action {
  color: #fff;
  width: 34px;
  height: 34px;
  background: #ffffff12;
}
.support-error {
  flex: 0 0 auto;
  margin: 10px 14px 0;
  border-radius: 12px;
  font-size: 12px;
}
.support-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  color: #24364b !important;
  scrollbar-width: thin;
}
.support-inbox,
.support-new-form {
  padding: 26px 22px 20px !important;
}
.support-welcome {
  margin-bottom: 22px;
}
.support-eyebrow {
  color: #6d8292;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.4px;
}
.support-welcome h3 {
  font-size: 23px;
  line-height: 1.3;
  font-weight: 650;
  letter-spacing: -0.6px;
  margin: 8px 0;
  color: #20384d;
}
.support-welcome p {
  color: #758391;
  font-size: 13px;
  line-height: 1.6;
  margin: 0;
}
.support-chat ::v-deep .v-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
}
.support-new {
  justify-content: space-between;
  padding: 0 20px !important;
  height: 48px !important;
  box-shadow: 0 4px 12px #15496218 !important;
}
.support-new ::v-deep .v-btn__content {
  justify-content: space-between;
}
.support-inbox-tools {
  margin: 22px 0 6px;
}
.support-filter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 15px;
  color: #84909c;
  font-size: 11px;
}
.support-filter select {
  background: #f2f6f8;
  border-radius: 20px;
  padding: 7px 28px 7px 13px;
  color: #41566a;
  font-size: 12px;
  appearance: auto;
  cursor: pointer;
  border: 1px solid #e7edf0;
}
.support-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 26px 0 9px;
  font-size: 12px;
  font-weight: 650;
  color: #586d80;
}
.support-section-label span {
  background: #f0f4f6;
  color: #83919d;
  font-size: 10px;
  border-radius: 8px;
  padding: 0 6px;
}
.support-conversation-list {
  padding: 0;
  background: transparent;
}
.support-conversation-row {
  padding: 13px 0 !important;
  gap: 11px;
  border-bottom: 1px solid #f0f3f5;
}
.support-conversation-row ::v-deep .v-list-item__content {
  padding: 0;
  min-width: 0;
}
.support-conversation-row ::v-deep .v-list-item__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 650;
  line-height: 1.6;
}
.support-conversation-row ::v-deep .v-list-item__title > span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
}
.support-avatar {
  display: grid;
  place-items: center;
  width: 37px;
  height: 37px;
  border-radius: 13px;
  background: #eaf3f5;
  color: #387080;
  font-size: 11px;
  font-weight: 700;
  flex: 0 0 auto;
}
.support-unread {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16818a;
  flex: 0 0 auto;
}
.support-preview {
  font-size: 12px;
  color: #7d8a96 !important;
  margin-top: 2px;
}
.support-row-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  color: #8996a0;
  font-size: 10px;
  margin-top: 5px;
  line-height: 1.5;
}
.support-row-meta span + span::before {
  content: "·";
  margin-right: 6px;
}
.support-empty {
  text-align: center;
  color: #81909d;
  padding: 34px 18px 14px;
}
.support-empty-icon {
  width: 62px;
  height: 62px;
  border-radius: 22px;
  background: #f1f6f7;
  display: grid;
  place-items: center;
  margin: 0 auto 15px;
}
.support-empty-icon .v-icon {
  color: #97afb9;
}
.support-empty strong {
  display: block;
  color: #5e7385;
  font-size: 13px;
  font-weight: 600;
}
.support-empty p {
  font-size: 12px;
  line-height: 1.7;
  max-width: 250px;
  margin: 7px auto 0;
}
.support-footnote {
  text-align: center;
  font-size: 10px;
  line-height: 1.7;
  color: #94a0aa;
  margin: 22px 0 0;
}
.support-form-field ::v-deep fieldset {
  border-color: #dfe7ec;
}
.support-form-field ::v-deep .v-input__slot {
  border-radius: 15px !important;
  background: #fcfdfe;
}
.support-form-field ::v-deep label {
  font-size: 13px;
}
.support-identity-note {
  font-size: 11px;
  line-height: 1.7;
  color: #85919b;
  margin: -2px 0 24px;
}
.support-metadata {
  flex: 0 0 auto;
  padding: 13px 18px 11px;
  border-bottom: 1px solid #edf1f4;
  overflow-wrap: anywhere;
}
.support-contact {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.support-contact > div:last-child {
  min-width: 0;
}
.support-contact strong {
  display: block;
  font-size: 12px;
  line-height: 1.5;
}
.support-contact span {
  display: block;
  font-size: 10px;
  color: #81909d;
  line-height: 1.6;
}
.support-identity-badge {
  display: inline-block;
  border-radius: 8px;
  background: #edf6f3;
  color: #47816b;
  padding: 3px 7px;
  font-size: 10px;
  line-height: 1.5;
}
.support-identity-visitor {
  background: #faf3e6;
  color: #9b7b3a;
}
.support-identity-warning {
  color: #917e5a;
  font-size: 10px;
  line-height: 1.4;
  margin: 5px 0 0;
}
.support-assignment {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
}
.support-assignment > span {
  color: #83919c;
  font-size: 10px;
  max-width: 45%;
}
.support-assignment .v-btn {
  font-size: 10px;
  min-width: 0;
  padding: 0 9px !important;
  height: 25px !important;
  color: #587589;
}
.support-assignment .v-btn--outlined {
  border-color: #dce6eb;
}
.support-history {
  background: #f7f9fb;
  padding: 18px !important;
}
.support-history-intro {
  text-align: center;
  margin: 0 0 22px;
}
.support-history-intro span {
  color: #81919e;
  font-size: 10px;
  font-weight: 600;
}
.support-history-intro p {
  color: #9aa6b0;
  font-size: 10px;
  margin: 3px 0 0;
}
.support-message {
  width: fit-content;
  max-width: 89%;
  padding: 11px 14px 8px;
  margin: 0 30px 14px 0;
  border-radius: 17px 17px 17px 4px;
  background: #fff;
  box-shadow: 0 2px 5px #20384d06;
  border: 1px solid #edf1f5;
  overflow-wrap: anywhere;
}
.support-message-own {
  margin: 0 0 14px auto;
  background: #e6f1f4;
  border-color: #e2eef1;
  border-radius: 17px 17px 4px 17px;
}
.support-author {
  display: block;
  font-size: 10px;
  font-weight: 600;
  color: #62808f;
  line-height: 1.5;
}
.support-text {
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 1.7;
  color: #314c60;
  margin: 5px 0 !important;
}
.support-time {
  display: block;
  font-size: 9px;
  line-height: 1.7;
  color: #97a6b0;
  text-align: right;
}
.support-image {
  display: block;
  max-width: 100%;
  max-height: 280px;
  object-fit: contain;
  border-radius: 11px;
  margin-top: 6px;
}
.support-composer {
  flex: 0 0 auto;
  padding: 13px 16px 10px;
  background: #fff;
}
.support-composer-box {
  border: 1px solid #e1e8ed;
  border-radius: 19px;
  padding: 12px 12px 7px;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.support-composer-box:focus-within {
  border-color: #71a5b2;
  box-shadow: 0 0 0 3px #2073880c;
}
.support-composer textarea {
  display: block;
  width: 100%;
  min-height: 45px;
  max-height: 130px;
  padding: 0 3px;
  font-size: 13px;
  line-height: 1.6;
  color: #314c60;
  resize: vertical;
  outline: none;
}
.support-composer textarea::placeholder {
  color: #9aa7b1;
}
.support-composer-actions {
  display: flex;
  align-items: center;
  gap: 7px;
}
.support-attach {
  color: #6e8597 !important;
  width: 32px;
  height: 32px;
}
.support-composer-hint {
  flex: 1;
  font-size: 10px;
  color: #a0abb4;
}
.support-chat .support-send {
  background: #1c6f7f;
  color: #fff !important;
  width: 35px;
  height: 35px;
  border-radius: 50%;
}
.support-chat .support-send.v-btn--disabled {
  background: #eff3f6 !important;
  color: #b5c0c8 !important;
}
.support-composer .support-footnote {
  margin-top: 7px;
  font-size: 9px;
}
.support-composer-note {
  color: #8b98a3;
  font-size: 11px;
  margin: 0 0 7px;
}
.support-file-input {
  display: none;
}
.support-attachment-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f4f8fa;
  border-radius: 11px;
  padding: 7px;
  margin-bottom: 10px;
}
.support-attachment-preview img {
  width: 43px;
  height: 43px;
  border-radius: 8px;
  object-fit: cover;
}
.support-attachment-preview > div {
  flex: 1;
  min-width: 0;
}
.support-attachment-preview strong {
  display: block;
  font-size: 11px;
  color: #597384;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.support-attachment-preview span {
  display: block;
  color: #94a2ad;
  font-size: 10px;
  margin-top: 3px;
}
.support-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.support-link {
  color: #13738a;
  text-decoration: underline;
  text-align: inherit;
  overflow-wrap: anywhere;
}
.support-link:focus-visible {
  outline: 2px solid #187385;
  outline-offset: 2px;
}
.support-destination {
  overflow-wrap: anywhere;
  unicode-bidi: isolate;
  direction: ltr;
}
.support-link-card {
  border-radius: 22px !important;
  padding: 10px 4px;
}
/* VDialog is detached from the page header/footer. Apply each inset once:
 * top on its own header; sides and bottom on the shared card, including the
 * inbox and visitor form. The composer keeps ordinary spacing only. */
.support-chat.support-chat-fullscreen {
  height: 100%;
  height: 100dvh;
  border-radius: 0 !important;
  /* Vuetify resets fullscreen cards with padding: 0 !important. Keep this
   * override scoped to our card and more specific than that vendor selector. */
  padding: 0 var(--support-safe-right) var(--support-safe-bottom)
    var(--support-safe-left) !important;
}
.support-chat-fullscreen .support-header {
  padding: calc(18px + var(--support-safe-top)) 18px 18px;
}
.support-chat-fullscreen .support-inbox,
.support-chat-fullscreen .support-new-form {
  padding: 24px 20px !important;
}
.support-chat-fullscreen .support-composer textarea {
  font-size: 16px;
}
.support-chat-fullscreen .support-history {
  padding: 15px !important;
}
@media (max-height: 500px) and (max-width: 960px) {
  /* Short landscape windows must keep every control reachable, even when the
   * administrator metadata and keyboard leave too little room for flex history. */
  .support-chat-fullscreen {
    overflow-y: auto;
  }
  .support-chat-fullscreen .support-body {
    flex: 0 0 auto;
    min-height: 150px;
    max-height: 60vh;
    max-height: 60dvh;
  }
  .support-chat-fullscreen .support-header {
    padding-top: calc(12px + var(--support-safe-top));
    padding-bottom: 12px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .support-toggle-track span,
  .support-composer-box {
    transition: none;
  }
}
</style>
<style>
.v-dialog.support-dialog {
  border-radius: 24px;
  box-shadow: 0 18px 70px #152c4940;
}
.v-dialog.support-link-dialog {
  border-radius: 22px;
}
.v-dialog.support-dialog.v-dialog--fullscreen {
  border-radius: 0;
}
</style>
